/**
 * Spatial Analysis Panel
 *
 * Main orchestrator component for spatial analysis tools.
 * Manages tool activation and execution of spatial operations.
 *
 * **Validates: Requirements 22.1, 23.1, 24.1, 25.1, 26.1, 26.4, 26.6**
 */

import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState, AppDispatch } from '../../store';
import { addLayer, setActiveLayer } from '../../store/slices/layersSlice';
import {
  setProcessing,
  setError,
  setMessage,
  setQueryResults,
  closeTool,
} from '../../store/slices/spatialAnalysisSlice';
import { addNotification } from '../../store/slices/uiSlice';
import { spatialAnalysisEngine } from '../../services/SpatialAnalysisEngine';
import type { VectorLayerData } from '../../services/SpatialAnalysisEngine';
import type { Feature } from 'geojson';
import BufferToolDialog from './BufferToolDialog';
import IntersectionToolDialog from './IntersectionToolDialog';
import UnionToolDialog from './UnionToolDialog';
import ClipToolDialog from './ClipToolDialog';
import SpatialQueryDialog from './SpatialQueryDialog';
import { mapNavigator } from '../../services/MapNavigator';

/**
 * Converts a Redux layer to VectorLayerData for the spatial engine
 */
function toVectorLayerData(layer: any): VectorLayerData {
  return {
    id: layer.id,
    name: layer.name,
    geometryType: layer.geometryType,
    features: (layer.features || []).map((f: any) => ({
      type: 'Feature' as const,
      id: f.id,
      geometry: f.geometry,
      properties: f.properties || {},
    })),
  };
}

export const SpatialAnalysisPanel: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    activeTool,
    bufferTool,
    intersectionTool,
    unionTool,
    clipTool,
    spatialQueryTool,
    queryResults,
  } = useSelector((state: RootState) => state.spatialAnalysis);
  const layers = useSelector((state: RootState) => state.layers.layers);

  // Generate a unique ID for new result layers
  const generateLayerId = () =>
    `spatial-result-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const finishResultLayer = useCallback(
    (
      id: string,
      name: string,
      geometryType: 'Point' | 'LineString' | 'Polygon' | 'MultiPolygon',
      features: Feature[],
      successMsg: string
    ) => {
      const mapped = features.map((f, i) => ({
        id: `${id}-f${i}`,
        layerId: id,
        geometry: f.geometry as GeoJSON.Geometry,
        properties: (f.properties || {}) as Record<string, unknown>,
      }));
      const layer = {
        id,
        name,
        type: 'vector' as const,
        geometryType,
        visible: true,
        opacity: 0.7,
        zIndex: layers.length + 1,
        featureCount: mapped.length,
        features: mapped,
      };
      dispatch(addLayer(layer));
      dispatch(setActiveLayer(id));
      dispatch(setMessage(successMsg));
      dispatch(addNotification({ type: 'success', message: successMsg }));
      dispatch(closeTool());
      // Zoom after next tick so map has the layer
      window.setTimeout(() => {
        mapNavigator.zoomToLayer(id, [...layers, layer]);
      }, 100);
    },
    [dispatch, layers]
  );

  // ---------------------------------------------------------------------------
  // Buffer execution
  // ---------------------------------------------------------------------------
  const handleBufferExecute = useCallback(() => {
    const sourceLayer = layers.find((l) => l.id === bufferTool.sourceLayerId);
    if (!sourceLayer) {
      dispatch(setError('Source layer not found'));
      return;
    }
    if (!sourceLayer.features?.length) {
      dispatch(setError('Source layer has no features. Import or digitize data first.'));
      return;
    }

    dispatch(setProcessing(true));

    const layerData = toVectorLayerData(sourceLayer);
    const result = spatialAnalysisEngine.buffer(layerData.features, {
      distance: bufferTool.distance,
      units: bufferTool.units,
      rings: bufferTool.rings,
    });

    dispatch(setProcessing(false));

    if (result.success && result.features.length > 0) {
      const id = generateLayerId();
      finishResultLayer(
        id,
        `Buffer - ${sourceLayer.name} (${bufferTool.distance} ${bufferTool.units})`,
        'Polygon',
        result.features,
        result.message || `Buffer created (${result.features.length} features)`
      );
    } else {
      const msg = result.message || 'Buffer operation failed';
      dispatch(setError(msg));
      dispatch(addNotification({ type: 'error', message: msg }));
    }
  }, [dispatch, layers, bufferTool, finishResultLayer]);

  // ---------------------------------------------------------------------------
  // Intersection execution
  // ---------------------------------------------------------------------------
  const handleIntersectionExecute = useCallback(() => {
    const sourceLayer = layers.find((l) => l.id === intersectionTool.sourceLayerId);
    const targetLayer = layers.find((l) => l.id === intersectionTool.targetLayerId);

    if (!sourceLayer || !targetLayer) {
      dispatch(setError('Source or target layer not found'));
      return;
    }

    dispatch(setProcessing(true));
    const result = spatialAnalysisEngine.intersection(
      toVectorLayerData(sourceLayer),
      toVectorLayerData(targetLayer)
    );
    dispatch(setProcessing(false));

    if (result.success && result.features.length > 0) {
      finishResultLayer(
        generateLayerId(),
        `Intersection - ${sourceLayer.name} ∩ ${targetLayer.name}`,
        'Polygon',
        result.features,
        result.message || `Intersection: ${result.features.length} feature(s)`
      );
    } else if (result.success) {
      dispatch(setMessage(result.message || 'No intersections found'));
      dispatch(addNotification({ type: 'info', message: 'No intersections found' }));
    } else {
      const msg = result.message || 'Intersection failed';
      dispatch(setError(msg));
      dispatch(addNotification({ type: 'error', message: msg }));
    }
  }, [dispatch, layers, intersectionTool, finishResultLayer]);

  // ---------------------------------------------------------------------------
  // Union execution
  // ---------------------------------------------------------------------------
  const handleUnionExecute = useCallback(() => {
    const selectedLayers = layers.filter((l) => unionTool.selectedLayerIds.includes(l.id));

    if (selectedLayers.length < 2) {
      dispatch(setError('Select at least two layers for union'));
      return;
    }

    dispatch(setProcessing(true));
    const result = spatialAnalysisEngine.union(selectedLayers.map(toVectorLayerData));
    dispatch(setProcessing(false));

    if (result.success && result.features.length > 0) {
      const layerNames = selectedLayers.map((l) => l.name).join(' ∪ ');
      finishResultLayer(
        generateLayerId(),
        `Union - ${layerNames}`,
        'Polygon',
        result.features,
        result.message || 'Union created'
      );
    } else {
      const msg = result.message || 'Union failed';
      dispatch(setError(msg));
      dispatch(addNotification({ type: 'error', message: msg }));
    }
  }, [dispatch, layers, unionTool, finishResultLayer]);

  // ---------------------------------------------------------------------------
  // Clip execution
  // ---------------------------------------------------------------------------
  const handleClipExecute = useCallback(() => {
    const inputLayer = layers.find((l) => l.id === clipTool.inputLayerId);
    const clipLayer = layers.find((l) => l.id === clipTool.clipLayerId);

    if (!inputLayer || !clipLayer) {
      dispatch(setError('Input or clip layer not found'));
      return;
    }

    dispatch(setProcessing(true));
    const result = spatialAnalysisEngine.clip(
      toVectorLayerData(inputLayer),
      toVectorLayerData(clipLayer)
    );
    dispatch(setProcessing(false));

    if (result.success && result.features.length > 0) {
      finishResultLayer(
        generateLayerId(),
        `Clip - ${inputLayer.name} by ${clipLayer.name}`,
        (inputLayer.geometryType as 'Polygon') || 'Polygon',
        result.features,
        result.message || 'Clip completed'
      );
    } else {
      const msg = result.message || 'Clip produced no results';
      dispatch(setError(msg));
      dispatch(addNotification({ type: 'error', message: msg }));
    }
  }, [dispatch, layers, clipTool, finishResultLayer]);

  // ---------------------------------------------------------------------------
  // Spatial Query execution
  // ---------------------------------------------------------------------------
  const handleSpatialQueryExecute = useCallback(() => {
    const referenceLayer = layers.find((l) => l.id === spatialQueryTool.referenceLayerId);
    const targetLayer = layers.find((l) => l.id === spatialQueryTool.targetLayerId);

    if (!referenceLayer || !targetLayer) {
      dispatch(setError('Reference or target layer not found'));
      return;
    }

    const referenceFeature = referenceLayer.features?.find(
      (f) => f.id === spatialQueryTool.referenceFeatureId
    );

    if (!referenceFeature) {
      dispatch(setError('Reference feature not found'));
      return;
    }

    dispatch(setProcessing(true));

    const result = spatialAnalysisEngine.spatialQuery({
      referenceFeature: {
        type: 'Feature',
        id: referenceFeature.id,
        geometry: referenceFeature.geometry,
        properties: referenceFeature.properties || {},
      },
      targetLayer: toVectorLayerData(targetLayer),
      relationship: spatialQueryTool.relationship,
      distance: spatialQueryTool.distance,
      distanceUnits: spatialQueryTool.distanceUnits,
    });

    dispatch(setProcessing(false));

    if (result.success) {
      dispatch(setQueryResults(result.features));
      dispatch(setMessage(result.message || ''));
    } else {
      dispatch(setError(result.message || 'Spatial query failed'));
    }
  }, [dispatch, layers, spatialQueryTool]);

  // ---------------------------------------------------------------------------
  // Export query results as new layer (Requirement 26.6)
  // ---------------------------------------------------------------------------
  const handleExportQueryResults = useCallback(() => {
    const targetLayer = layers.find((l) => l.id === spatialQueryTool.targetLayerId);
    const results = queryResults;

    if (results.length === 0) {
      dispatch(setError('No results to export'));
      return;
    }

    dispatch(
      addLayer({
        id: generateLayerId(),
        name: `Query Results - ${spatialQueryTool.relationship}`,
        type: 'vector',
        geometryType: targetLayer?.geometryType || 'Polygon',
        visible: true,
        opacity: 0.8,
        zIndex: layers.length,
        featureCount: results.length,
        features: results.map((f: Feature, i: number) => ({
          id: `query-result-${i}-${Date.now()}`,
          layerId: '',
          geometry: f.geometry,
          properties: f.properties || {},
        })),
      })
    );
    dispatch(setMessage(`Exported ${results.length} features as new layer`));
  }, [dispatch, layers, spatialQueryTool, queryResults]);

  return (
    <>
      <BufferToolDialog open={activeTool === 'buffer'} onExecute={handleBufferExecute} />
      <IntersectionToolDialog
        open={activeTool === 'intersection'}
        onExecute={handleIntersectionExecute}
      />
      <UnionToolDialog open={activeTool === 'union'} onExecute={handleUnionExecute} />
      <ClipToolDialog open={activeTool === 'clip'} onExecute={handleClipExecute} />
      <SpatialQueryDialog
        open={activeTool === 'spatial-query'}
        onExecute={handleSpatialQueryExecute}
        onExportResults={handleExportQueryResults}
      />
    </>
  );
};

export default SpatialAnalysisPanel;
